import { queryOptions, useQuery } from '@tanstack/react-query'
import { getOrganizationVirtualCardTypes } from 'src/queries/organizations/virtualCardTypes/virtualCardTypes.network'
import { orgQueryKeys } from 'src/queries/queries.constants'
import { ExtendBaseQueryOptions } from 'src/queries/queries.utils'

import { VirtualCardTypesResponse } from './virtualCardTypes.types'

export const organizationVirtualCardTypesQueries = {
  detail: (orgId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.virtualCardTypes(orgId),
      queryFn: () => {
        // enabled gates the initial fetch, but refetch()/enabled overrides
        // can still reach queryFn without an orgId — guard instead of
        // asserting so we fail loudly rather than request
        // `organizations/undefined/virtualcardtypes`.
        if (!orgId) {
          throw new Error(
            'organizationVirtualCardTypesQueries.detail: orgId is required',
          )
        }
        return getOrganizationVirtualCardTypes(orgId)
      },
      enabled: !!orgId,
    }),
}

interface OrganizationVirtualCardTypesOptions
  extends ExtendBaseQueryOptions<VirtualCardTypesResponse, string[]> {
  orgId: string | undefined
}

export const useQueryOrganizationVirtualCardTypes = ({
  orgId,
  enabled = true,
  queryCacheOptions,
  ...rest
}: OrganizationVirtualCardTypesOptions) => {
  return useQuery({
    ...organizationVirtualCardTypesQueries.detail(orgId),
    select: (data: VirtualCardTypesResponse) => data.virtualCardTypes,
    // spread overrides replace `enabled`, so re-apply the orgId presence guard.
    enabled: enabled && !!orgId,
    ...queryCacheOptions,
    ...rest,
  })
}

interface OrganizationBillPayEnabledOptions
  extends ExtendBaseQueryOptions<VirtualCardTypesResponse, boolean> {
  orgId: string | undefined
}

// Org-level Bill Pay entry-point gate. Funding-source-specific behavior, like
// declining-balance amount locking, comes from /creditcards features.
export const useQueryOrganizationBillPayEnabled = ({
  orgId,
  enabled = true,
  queryCacheOptions,
  ...rest
}: OrganizationBillPayEnabledOptions) => {
  return useQuery({
    ...organizationVirtualCardTypesQueries.detail(orgId),
    select: (data: VirtualCardTypesResponse) => data.billPayEnabled,
    // spread overrides replace `enabled`, so re-apply the orgId presence guard.
    enabled: enabled && !!orgId,
    ...queryCacheOptions,
    ...rest,
  })
}
